import { Box, Accordion, AccordionSummary, Breadcrumbs } from "@mui/material";
import { useEffect, useState } from "react";
import "Styles/MedicalRecords.css";
import cloneDeep from "lodash/cloneDeep";
import Pagination from "rc-pagination";
import "rc-pagination/assets/index.css";
import Vitalsigns1 from "assets/images/Vitalsigns.png";
import HealthRecordHeader from "components/HealthRecord/HealthRecordHeader";
import { KMedicalRecordType } from "utilities/constants";
import { useGetVitalSignsQuery } from "api/MedicalRecordApi";
import useAuth from "hooks/useAuth";
import { stringify } from "qs";
import DefaultLoader from "components/UI/DefaultLoader";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { NavigateNext } from "@mui/icons-material";
import { BCPatientVitalSigns } from "components/UI/BreadcrumbsList";
import CPagination from "components/UI/CPagination";
import NoHealthRecords from "components/UI/NoHealthRecords";

const groupBy = [
  { name: "None", value: "none", label: "None" },
  { name: "Measurement", value: "code", label: "Measurement" },
  { name: "Year", value: "year", label: "Year" },
];

const VitalSign = () => {
  const { accessToken } = useAuth();

  const [groupedBy, setGroupedBy] = useState("none");
  const countPerPage = 10;
  const [groupbyCurrentPage, setGroupByCurrentPage] = useState(1);
  const [groupByTitle, setGroupbyTitle] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const [groupbycollection, setGroupbyCollection] = useState(
    cloneDeep([].slice(0, countPerPage))
  );

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [groupTitle, setGroupTitle] = useState([]);
  const [hcps, setHcps] = useState([]);
  const [encounters, setEncounters] = useState([]);
  const [code, setCode] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [mostRecentDataChecked, setMostRecentDataChecked] = useState(false);
  const [categories, setCategories] = useState([]);

  const {
    data: vitalSign,
    isLoading: loading,
    isFetching,
  } = useGetVitalSignsQuery(
    {
      accessToken,
      page,
      perPage,
      query: `&${stringify({
        group_by: groupedBy === "none" ? undefined : groupedBy,
        code: code.length === 0 ? undefined : code.join(","),
        from_date:
          fromDate === null
            ? undefined
            : new Intl.DateTimeFormat("en-US").format(fromDate),
        to_date:
          toDate === null
            ? undefined
            : new Intl.DateTimeFormat("en-US").format(toDate),
        healthcare_provider_ids: hcps?.length === 0 ? undefined : hcps?.join(","),
        encounter: encounters?.length === 0 ? undefined : encounters?.join(","),
        category: categories?.length === 0 ? undefined : categories?.join(","),
      })}`,
    },
    { refetchOnMountOrArgChange: true, refetchOnReconnect: true }
  );

  useEffect(() => {
    setExpanded(false);
  }, [groupedBy]);

  const handleGroupBy = (e) => {
    setGroupedBy(e.value);
  };

  useEffect(() => {
    if (!Array.isArray(vitalSign?.observations)) {
      setGroupTitle(Object.keys(vitalSign?.observations || []));
    }
  }, [vitalSign]);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
    setGroupByCurrentPage(1);
  };

  const groupbyUpdatePage = (p) => {
    setGroupByCurrentPage(p);
    const to = countPerPage * p;
    const from = to - countPerPage;
    setGroupbyCollection(
      cloneDeep(vitalSign?.observations[groupByTitle]?.slice(from, to))
    );
  };

  return (
    <Box sx={{ fontFamily: "Open Sans" }}>
      <Breadcrumbs
        separator={<NavigateNext fontSize="small" />}
        aria-label="breadcrumb"
      >
        {BCPatientVitalSigns}
      </Breadcrumbs>
      <Box>
        <Box>
          <HealthRecordHeader
            groupBy={groupBy}
            handleGroupBy={handleGroupBy}
            icon={Vitalsigns1}
            medicalRecordType={vitalSign}
            title={KMedicalRecordType.VITAL_SIGNS}
            code={code}
            setCode={setCode}
            fromDate={fromDate}
            toDate={toDate}
            setFromDate={setFromDate}
            setToDate={setToDate}
            mostRecentDataChecked={mostRecentDataChecked}
            setMostRecentDataChecked={setMostRecentDataChecked}
            hcps={hcps}
            setHcps={setHcps}
            encounters={encounters}
            setEncounters={setEncounters}
            categories={categories}
            setCategories={setCategories}
          />
        </Box>
        {loading || isFetching ? (
          <DefaultLoader />
        ) : vitalSign?.observations?.length !== 0 ? (
          <Box sx={{ mt: "1rem" }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(6, 1fr)",
                gap: "1rem",
                px: "1rem",
                py: ".25rem",
                mb: "0.625rem",
                fontSize: "1rem",
                color: "#8e8e8e",
              }}
            >
              <Box>Onset Date</Box>
              <Box>Measurement</Box>
              <Box>Encounter Type</Box>
              <Box>Provider</Box>
              <Box>Value</Box>
              <Box>Performers</Box>
            </Box>

            {!Array.isArray(vitalSign?.observations) ? (
              groupTitle?.length !== 0 ? (
                groupTitle?.map((title, index) => (
                  <Accordion
                    key={title}
                    elevation={0}
                    expanded={expanded === `panel${index + 1}`}
                    onChange={handleAccordionChange(`panel${index + 1}`)}
                    disableGutters={true}
                    sx={{
                      borderTop: "1px solid rgb(0,0,0,0.125)",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon color="primary" />}
                      aria-controls={`panel${index + 1}a-content`}
                      id={`panel${index + 1}a-header`}
                      onClick={() => {
                        setGroupbyCollection(
                          cloneDeep(
                            vitalSign?.observations[title]?.slice(0, 10)
                          )
                        );
                        setGroupbyTitle(title);
                      }}
                      sx={{ color: "primary.main", bgcolor: "#dcf0ff" }}
                    >
                      {title +
                        " (" +
                        vitalSign?.observations[title]?.length +
                        ")"}
                    </AccordionSummary>
                    <Box sx={{ mt: ".25rem", display: "grid", gap: ".25rem" }}>
                      {groupbycollection?.map((item) => (
                        <RecordCard key={item?.id} item={item} />
                      ))}
                      <Pagination
                        pageSize={10}
                        onChange={(p) => {
                          groupbyUpdatePage(p);
                        }}
                        className="align-pagination mt-3 mb-3"
                        current={groupbyCurrentPage}
                        total={vitalSign?.observations[title]?.length}
                        showTitle={false}
                      />
                    </Box>
                  </Accordion>
                ))
              ) : (
                <NoHealthRecords />
              )
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gap: "0.625rem",
                  fontSize: "0.875rem",
                  mb: "1rem",
                }}
              >
                {vitalSign?.observations?.map((item) => (
                  <RecordCard key={item?.id} item={item} />
                ))}
              </Box>
            )}
            {groupedBy === "none" && (
              <CPagination
                page={page}
                perPage={perPage}
                setPage={setPage}
                setPerPage={setPerPage}
                totalCount={vitalSign?.total_records}
              />
            )}
          </Box>
        ) : (
          <NoHealthRecords />
        )}
      </Box>
    </Box>
  );
};

export default VitalSign;

const RecordCard = ({ item }) => {
  const value =
    item.value_quantities?.length === 1
      ? item.value_quantities[0]?.value + " " + item.value_quantities[0].unit
      : item.value_quantities?.length === 2
      ? item.value_quantities[0]?.value +
        "/" +
        item?.value_quantities[1]?.value +
        " " +
        item?.value_quantities[1]?.unit
      : "";

  return (
    <Box
      sx={{
        p: "1rem",
        borderRadius: "5px",
        bgcolor: "#f8f8f8",
        display: "grid",
        gridTemplateColumns: "repeat(6, 1fr)",
        gap: "1rem",
        "& > div": {
          whiteSpace: "nowrap",
          width: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      }}
    >
      <Box component={"div"} title={item?.effective_datetime}>
        {item?.effective_datetime}
      </Box>
      <Box component={"div"} title={item?.code}>
        {item?.code}
      </Box>
      <Box component={"div"} title={item?.encounter}>
        {item?.encounter}
      </Box>
      <Box component={"div"} title={item?.heathcare_provider_name}>
        {item?.heathcare_provider_name}
      </Box>
      <Box component={"div"} title={value}>
        {value}
      </Box>
      <Box component={"div"} title={item?.performers[0]}>
        {item?.performers[0]}
      </Box>
    </Box>
  );
};
